import { AppPasswordRequirementSettings } from '@models/Settings.model';

/**
 * Presentation helpers for the tenant password policy
 * (/settings/public → passwordRequirement).
 *
 * Companion to CustomValidators.passwordRequirement / passwordLength — the
 * error keys here match the keys those validators emit.
 */

/** Single source of truth for the character-class checks — shared by the
 *  validator (CustomValidators) and the checklist (passwordRules). */
export const PASSWORD_CHARSET_REGEX = {
    upperCase: /[A-Z]/,
    lowerCase: /[a-z]/,
    number: /[0-9]/,
    symbol: /[^A-Za-z0-9]/
} as const;

export interface PasswordRule {
    label: string;
    isMet: (value: string) => boolean;
}

/**
 * The policy as a checklist — one entry per active rule, each testable
 * against the current input. Rendered live by PasswordRequirementsComponent
 * so the user always sees ALL rules and their pass/fail state, not just the
 * first failing one.
 */
export function passwordRules(req: AppPasswordRequirementSettings): PasswordRule[] {
    const rules: PasswordRule[] = [
        {
            label: `${req.passwordMinLength}–${req.passwordMaxLength} characters`,
            isMet: (v) => v.length >= req.passwordMinLength && v.length <= req.passwordMaxLength
        }
    ];
    if (req.passwordRequireUpperCase) {
        rules.push({
            label: 'At least one uppercase letter',
            isMet: (v) => PASSWORD_CHARSET_REGEX.upperCase.test(v)
        });
    }
    if (req.passwordRequireLowerCase) {
        rules.push({
            label: 'At least one lowercase letter',
            isMet: (v) => PASSWORD_CHARSET_REGEX.lowerCase.test(v)
        });
    }
    if (req.passwordRequireNumber) {
        rules.push({
            label: 'At least one number',
            isMet: (v) => PASSWORD_CHARSET_REGEX.number.test(v)
        });
    }
    if (req.passwordRequireSymbol) {
        rules.push({
            label: 'At least one symbol',
            isMet: (v) => PASSWORD_CHARSET_REGEX.symbol.test(v)
        });
    }
    return rules;
}

/**
 * Validator-key → message map for a password field, built from the policy.
 * The min/max messages embed the tenant's configured values, which the static
 * FIELD_ERROR_MESSAGES registry cannot express, so pass these via the
 * `[messages]` input on `app-field-error`. Used at login, where the checklist
 * is not shown.
 */
export function passwordErrorMessages(
    req: AppPasswordRequirementSettings
): Record<string, string> {
    return {
        required: 'Password is required.',
        minlength: `Password must be at least ${req.passwordMinLength} characters.`,
        maxlength: `Password cannot exceed ${req.passwordMaxLength} characters.`,
        passwordUpperCase: 'Password must contain at least one uppercase letter.',
        passwordLowerCase: 'Password must contain at least one lowercase letter.',
        passwordNumber: 'Password must contain at least one number.',
        passwordSymbol: 'Password must contain at least one symbol.'
    };
}

/**
 * Pass to `app-field-error` on fields that render app-password-requirements
 * alongside: the checklist already shows the policy failures live, so these
 * keys are suppressed (empty message = suppress) to avoid saying the same
 * thing twice. `required` and server errors still show normally.
 */
export const PASSWORD_ERRORS_SHOWN_IN_CHECKLIST: Record<string, string> = {
    minlength: '',
    maxlength: '',
    passwordUpperCase: '',
    passwordLowerCase: '',
    passwordNumber: '',
    passwordSymbol: ''
};
